import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const NORMAL_INTERVAL_MS = 30_000;   // routine background heartbeat
const RECOVERY_INTERVAL_MS = 3_000;  // fast poll once we know it's down

/**
 * MaintenanceOverlay - a full-screen "back in 60 seconds" curtain that
 * only appears when the backend / DB is momentarily unreachable
 * (typically during a redeploy). Runs a lightweight background ping to
 * `/api/healthz` every 30s. On the first failure we drop the curtain,
 * bump the poll to 3s, and hard-reload the moment the health check
 * comes back green so the user sees the freshly-deployed site.
 *
 * Zero interaction with any auth or fetch code - it's a top-level
 * watchdog that anyone can safely mount once inside <BrowserRouter>.
 */
const MaintenanceOverlay = () => {
  const [down, setDown] = useState(false);
  const consecutiveFailsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (cancelled) return;
      let ok = false;
      try {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 4000);
        const res = await fetch(`${API}/api/healthz`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        });
        clearTimeout(t);
        if (res.ok) {
          const j = await res.json().catch(() => ({}));
          ok = !!j.ok;
        }
      } catch { /* offline / abort / network */ }

      if (ok) {
        consecutiveFailsRef.current = 0;
        if (down) {
          // Recovery - reload once so the user gets the freshly-deployed
          // build with any new secrets / config picked up.
          window.location.reload();
          return;
        }
      } else {
        consecutiveFailsRef.current += 1;
        // Require 2 consecutive fails before showing so a single flaky
        // request doesn't scare readers with a scary overlay.
        if (!down && consecutiveFailsRef.current >= 2) setDown(true);
      }

      if (!cancelled) {
        const nextDelay = (down || consecutiveFailsRef.current > 0)
          ? RECOVERY_INTERVAL_MS
          : NORMAL_INTERVAL_MS;
        timerRef.current = setTimeout(check, nextDelay);
      }
    };

    // First heartbeat 20s after mount so we never race the initial paint.
    timerRef.current = setTimeout(check, 20_000);

    // Also probe immediately whenever the tab comes back to the foreground
    // (typical for someone returning from Instagram after a share).
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [down]);

  if (!down) return null;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      data-testid="maintenance-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm px-4"
    >
      <div className="max-w-md w-full text-center border border-red-600/60 bg-zinc-950 rounded-lg p-8 shadow-[0_0_60px_rgba(220,38,38,0.35)]">
        <ShieldCheck className="h-8 w-8 text-red-500 mx-auto mb-4" />
        <p className="text-red-400 text-[10px] font-black uppercase tracking-[0.35em] mb-3">
          Outlaw Media
        </p>
        <h1
          className="text-white text-2xl sm:text-3xl font-black leading-tight mb-3"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          Site's back in ~60 seconds.
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-6">
          We're applying constant security and privacy improvements. The site will refresh itself automatically the moment we're back - you don't need to do anything.
        </p>
        <div className="flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
          </span>
          Reconnecting
        </div>
      </div>
    </div>
  );
};

export default MaintenanceOverlay;
